import {
    CommissionOrderImport,
    ImportCommissionOrdersReturn,
} from '../../../features/order/orderAPI'
import styles from './../ImportCommissionOrders.module.scss'

import { ListView, ListViewHeader, ListViewItemProps } from '@progress/kendo-react-listview'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { Scrollbars } from 'react-custom-scrollbars-2'

import { LOADING_STATES } from '../../../features/meta/metaSlice'
import { COUNTRY_CODES } from '../../../consts'
import {
    DeleteLineCell,
    MoneyCell,
    PartNumberCell,
    QuantityCell,
    TextCell,
    CommissionPercentageCell,
} from './CustomCells'
import { CustomerSearchTextBox } from './CustomerSearchTextBox'
import { fqAlert } from '../../../GlobalComponents/NotificationManager/NotificationManager'

const PLACEHOLDER_ORDER_NUMBER = '9999999999'

interface ImportLineItemsProps {
    commissionsInput: CommissionOrderImport
    UpdateCommissionsInput: (dataItem?: CommissionOrderImport) => void
    commissionOrders: Array<ImportCommissionOrdersReturn>
    chosenCommissionOrder?: ImportCommissionOrdersReturn
    OnChosenCommissionOrderChange: (dataItem?: ImportCommissionOrdersReturn) => void
    importStatus: LOADING_STATES
    OnCorrectionMade: (dataItem: CommissionOrderImport) => void
}

export const LineItems = (props: ImportLineItemsProps) => {
    const {
        commissionsInput,
        UpdateCommissionsInput,
        commissionOrders,
        chosenCommissionOrder,
        OnChosenCommissionOrderChange,
        importStatus,
        OnCorrectionMade,
    } = props

    const OrderListHeader = () => {
        return (
            <ListViewHeader className={styles.orderListHeader}>
                Invoice Numbers (Customer Order Number)
            </ListViewHeader>
        )
    }

    const OrderListItem = (props: ListViewItemProps) => {
        const order = props.dataItem.Order

        const savedSuccessfully = order.OrderNumber !== PLACEHOLDER_ORDER_NUMBER

        const anyProblemsHaveSeverity = (problems: any, severity: any) =>
            problems.length > 0 && problems.find((p: any) => p.Severity === severity) !== undefined

        const errorExists =
            anyProblemsHaveSeverity(props.dataItem.Problems, 1) ||
            order.LineItems.find((l: any) => anyProblemsHaveSeverity(l.Problems, 1)) !== undefined

        const warningExists =
            !savedSuccessfully &&
            (anyProblemsHaveSeverity(props.dataItem.Problems, 0) ||
                order.LineItems.find((l: any) => anyProblemsHaveSeverity(l.Problems, 0)) !==
                    undefined)

        return (
            <div
                className={`${styles.orderListItem} k-listview-item${
                    chosenCommissionOrder === props.dataItem ? ` ${styles.chosen}` : ''
                }${errorExists ? ` ${styles.errorExists}` : ''}${
                    warningExists ? ` ${styles.warningExists}` : ''
                }${savedSuccessfully ? ` ${styles.savedSuccessfully}` : ''}`}
                onClick={() => OnChosenCommissionOrderChange(props.dataItem)}
            >
                {order.InvoiceNumber} ({order.CustomerOrderNumber})
                {savedSuccessfully ? ` - ${order.OrderNumber}` : ''}
                {!savedSuccessfully && (
                    <div
                        className={styles.deleteOrder}
                        onClick={() => {
                            const tempItems = commissionsInput!.CommissionOrderItems.filter(
                                (line) =>
                                    line.InvoiceNumber.Value !== props.dataItem.Order.InvoiceNumber
                            )
                            const temp = {
                                ...commissionsInput!,
                                CommissionOrderItems: tempItems,
                            }
                            UpdateCommissionsInput(temp)
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className='k-icon k-i-trash'></span>
                    </div>
                )}
            </div>
        )
    }

    const isNumeric = (value: string) => {
        return /^-?\d+\.?\d*$/.test(value)
    }

    const customerAccountCodeOnChange = (newVal: string) => {
        // Grab previous version of order
        const updatedVersionOfOrder = commissionOrders.filter(
            (c) => c.Order.InvoiceNumber === chosenCommissionOrder!.Order.InvoiceNumber
        )[0]
        updatedVersionOfOrder.Order.CustomerAccountCode = newVal.toUpperCase()

        // Update object to bind order information
        OnChosenCommissionOrderChange({
            ...updatedVersionOfOrder,
        })

        const updatedItems = commissionsInput!.CommissionOrderItems.map((item) =>
            item.InvoiceNumber.Value === updatedVersionOfOrder.Order.InvoiceNumber
                ? { ...item, CustomerAccountCode: { Value: newVal.trim().toUpperCase() } }
                : item
        )

        if (updatedItems.length > 0) {
            UpdateCommissionsInput({
                ...commissionsInput!,
                CommissionOrderItems: updatedItems,
            })
        }
    }

    const chosenCommissionOrderAlreadySaved =
        chosenCommissionOrder &&
        chosenCommissionOrder.Order.OrderNumber !== PLACEHOLDER_ORDER_NUMBER

    return (
        <div className={styles.importCommissionOrdersResultsContainer}>
            {importStatus === LOADING_STATES.DONE && (
                <>
                    <Scrollbars
                        className={styles.orderList}
                        renderTrackHorizontal={() => <div />}
                        autoHeight
                        autoHeightMin={640}
                        autoHeightMax={640}
                    >
                        <ListView
                            data={commissionOrders}
                            item={OrderListItem}
                            header={OrderListHeader}
                        />
                    </Scrollbars>
                    {chosenCommissionOrder && (
                        <>
                            <div className={styles.headerInfo}>
                                <div>
                                    <div>Customer Account Code:</div>
                                    <div
                                        className={`${styles.valueLine} ${styles.customerAccountCode}`}
                                    >
                                        <CustomerSearchTextBox
                                            onCustomerChange={customerAccountCodeOnChange}
                                            value={
                                                chosenCommissionOrder.Order.CustomerAccountCode ||
                                                ''
                                            }
                                            disabled={chosenCommissionOrderAlreadySaved}
                                        />
                                    </div>
                                    <div>Customer Order Number:</div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order.CustomerOrderNumber || ''}
                                    </div>
                                    <div>Invoice Number:</div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order.InvoiceNumber || ''}
                                    </div>
                                </div>
                                <div>
                                    <div>Ship Date:</div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order.InvoiceDate
                                            ? new Date(
                                                  chosenCommissionOrder.Order.InvoiceDate
                                              ).toLocaleDateString()
                                            : ''}
                                    </div>
                                    <div>Ship To:</div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order.ShipToName || ''}
                                    </div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order?.ShipToAddress1 || ''}
                                    </div>
                                    <div className={styles.valueLine}>
                                        {chosenCommissionOrder.Order?.ShipToCity || ''}
                                        {chosenCommissionOrder.Order?.ShipToCity && ', '}
                                        {chosenCommissionOrder.Order?.ShipToState?.trim() || ''}
                                        <span style={{ marginLeft: 4 }}>
                                            {chosenCommissionOrder.Order?.ShipToZipcode || ''}
                                        </span>
                                    </div>
                                    <div className={styles.valueLine}>
                                        {
                                            COUNTRY_CODES[
                                                chosenCommissionOrder.Order?.ShipToCountry
                                                    ? chosenCommissionOrder.Order.ShipToCountry.trim()
                                                    : ''
                                            ]
                                        }
                                    </div>
                                </div>
                                {chosenCommissionOrderAlreadySaved ? (
                                    <div className={styles.successInfo}>
                                        Saved successfully!
                                        <br />
                                        <br />
                                        Order Number:&nbsp;
                                        {chosenCommissionOrder.Order.OrderNumber}
                                    </div>
                                ) : (
                                    chosenCommissionOrder.Problems.length > 0 && (
                                        <div className={styles.errorsAndWarnings}>
                                            {chosenCommissionOrder.Problems.map((p) => {
                                                return (
                                                    <div
                                                        className={
                                                            p.Severity === 0
                                                                ? styles.warningInfo
                                                                : styles.errorInfo
                                                        }
                                                    >
                                                        {p.Message}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )
                                )}
                            </div>
                            <div className={styles.detailGrid}>
                                <Grid
                                    data={chosenCommissionOrder.Order?.LineItems.sort(
                                        (a, b) => a.Row - b.Row
                                    )}
                                    style={{ borderRadius: '7px', height: '515px' }}
                                    filterable={false}
                                    sortable={false}
                                    className='dateSection'
                                >
                                    <GridColumn
                                        cell={(props: any) =>
                                            TextCell(
                                                props.dataItem,
                                                false,
                                                'Row',
                                                'right',
                                                !chosenCommissionOrderAlreadySaved
                                            )
                                        }
                                        headerCell={() => (
                                            <div
                                                style={{
                                                    textAlign: 'right',
                                                    padding: '10px 24px',
                                                }}
                                            >
                                                Row
                                            </div>
                                        )}
                                        width={75}
                                    />
                                    <GridColumn
                                        cell={(props: any) => (
                                            <PartNumberCell
                                                dataItem={props.dataItem}
                                                customerAccountCode={
                                                    chosenCommissionOrder.Order?.CustomerAccountCode
                                                }
                                                OnChosenCommissionOrderChange={
                                                    OnChosenCommissionOrderChange
                                                }
                                                chosenCommissionOrder={chosenCommissionOrder}
                                                commissionsInput={commissionsInput}
                                                showProblems={!chosenCommissionOrderAlreadySaved}
                                                OnCorrectionMade={OnCorrectionMade}
                                            />
                                        )}
                                        headerCell={() => (
                                            <div style={{ padding: '10px 24px' }}>Part #</div>
                                        )}
                                        width={225}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            TextCell(props.dataItem, true, 'Description', 'left')
                                        }
                                        headerCell={() => (
                                            <div style={{ padding: '10px 24px' }}>Description</div>
                                        )}
                                        width={300}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            TextCell(
                                                props.dataItem,
                                                true,
                                                'CommissionProductCode',
                                                'center',
                                                !chosenCommissionOrderAlreadySaved,
                                                (newVal: string) => {
                                                    const updatedItem =
                                                        commissionsInput!.CommissionOrderItems.find(
                                                            (line) =>
                                                                line.Row.Value ===
                                                                props.dataItem.Row
                                                        )

                                                    if (updatedItem) {
                                                        updatedItem.CommissionProductCode.Value =
                                                            newVal.toString().trim().toUpperCase()
                                                        OnCorrectionMade({
                                                            ...commissionsInput!,
                                                            CommissionOrderItems: [updatedItem],
                                                        })
                                                    }
                                                }
                                            )
                                        }
                                        headerCell={() => (
                                            <div style={{ padding: '10px 24px' }}>Comm PC</div>
                                        )}
                                        width={125}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            CommissionPercentageCell(props.dataItem, (newVal) => {
                                                if (isNumeric(newVal)) {
                                                    const updatedItem =
                                                        commissionsInput!.CommissionOrderItems.find(
                                                            (line) =>
                                                                line.Row.Value ===
                                                                props.dataItem.Row
                                                        )

                                                    if (updatedItem) {
                                                        updatedItem.CommissionPercentage.Value =
                                                            parseFloat(newVal)
                                                        OnCorrectionMade({
                                                            ...commissionsInput!,
                                                            CommissionOrderItems: [updatedItem],
                                                        })
                                                    }
                                                }
                                            })
                                        }
                                        headerCell={() => (
                                            <div
                                                style={{
                                                    textAlign: 'right',
                                                    padding: '10px 24px',
                                                }}
                                            >
                                                Comm %
                                            </div>
                                        )}
                                        width={125}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            QuantityCell(
                                                props.dataItem,
                                                !chosenCommissionOrderAlreadySaved,
                                                (newVal) => {
                                                    if (isNumeric(newVal)) {
                                                        const updatedItem =
                                                            commissionsInput!.CommissionOrderItems.find(
                                                                (line) =>
                                                                    line.Row.Value ===
                                                                    props.dataItem.Row
                                                            )

                                                        if (updatedItem) {
                                                            updatedItem.OrderQuantity.Value =
                                                                parseFloat(newVal)
                                                            OnCorrectionMade({
                                                                ...commissionsInput!,
                                                                CommissionOrderItems: [updatedItem],
                                                            })
                                                        }
                                                    }
                                                }
                                            )
                                        }
                                        headerCell={() => (
                                            <div
                                                style={{
                                                    textAlign: 'right',
                                                    padding: '10px 24px',
                                                }}
                                            >
                                                Quantity
                                            </div>
                                        )}
                                        width={125}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            MoneyCell(props.dataItem, true, 'UnitPrice')
                                        }
                                        headerCell={() => (
                                            <div
                                                style={{
                                                    textAlign: 'right',
                                                    padding: '10px 24px',
                                                }}
                                            >
                                                Unit Price
                                            </div>
                                        )}
                                        width={125}
                                    />
                                    <GridColumn
                                        cell={(props: any) =>
                                            DeleteLineCell(
                                                (dataItem) => {
                                                    const tempItems =
                                                        commissionsInput!.CommissionOrderItems.filter(
                                                            (line) =>
                                                                line.Row.Value !== dataItem.Row
                                                        )

                                                    const tempItemsForThisInvoice =
                                                        tempItems.filter(
                                                            (line) =>
                                                                line.InvoiceNumber.Value ===
                                                                dataItem.LineItem.InvoiceNumber
                                                        )
                                                    if (tempItemsForThisInvoice.length === 0) {
                                                        fqAlert({
                                                            type: 'error',
                                                            title: 'At least one line item required to add to quote',
                                                        })
                                                    } else {
                                                        const temp = {
                                                            ...commissionsInput!,
                                                            CommissionOrderItems: tempItems,
                                                        }
                                                        UpdateCommissionsInput(temp)
                                                    }
                                                },
                                                props.dataItem,
                                                chosenCommissionOrderAlreadySaved
                                            )
                                        }
                                    />
                                </Grid>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}
